// eslint-disable-next-line import/no-extraneous-dependencies
import { BarChartOutlined, ThunderboltOutlined } from "@ant-design/icons";
import { useDraggable } from "@dnd-kit/core";
import { Button, Menu, Tooltip } from "antd";
import MenuDropdown from "components/MenuDropdown";
import React from "react";

// Helper: get position visual type
export const getPositionVisualType = (position: any) => {
  if (position.positionType === "key" && position.positionFunction === "identifier") {
    return { label: "Key", color: "#1976d2" };
  }
  if (position.positionFunction === "autoset" || position.positionFunction === "AUTOSET") {
    return { label: "Autoset", color: "#43a047" };
  }
  if (position.positionType === "zone" || position.positionFunction === "group") {
    return { label: "Group", color: "#6d4caf" };
  }
  return { label: "Other", color: "#aaa" };
};

export function DraggableAsset({
  asset,
  positionName,
  hasMeter,
  onLocate,
  onViewMeasurements,
  onViewMeterReadings,
  onEdit,
  isCTNode,
  meterIcon, // New prop for the meter icon with status
  // New props for menu functionality
  onCreateMeter,
  onUnmapMeter,
  hasActiveAssetMeter,
  isPC,
  phases, // New prop for number of phases
  onDeleteAsset,
}: any) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: asset.id });
  if (asset.deletedAt) return null;

  // Create the asset menu with all actions
  const assetMenu = (
    <Menu
      style={{ minWidth: 180 }}
      items={[
        {
          key: "edit-asset",
          label: "Edit Asset",
          disabled: !onEdit,
          onClick: () => {
            if (onEdit) onEdit();
          },
        },
        {
          type: "divider",
        },
        {
          key: "view-meter-readings",
          label: "View Meter Readings",
          disabled: !hasMeter || !onViewMeterReadings,
          onClick: () => {
            if (hasMeter && onViewMeterReadings) onViewMeterReadings();
          },
        },
        {
          key: "view-ct-measurements",
          label: "View CT Measurements",
          disabled: !hasMeter || !isCTNode || !onViewMeasurements,
          onClick: () => {
            if (hasMeter && isCTNode && onViewMeasurements) onViewMeasurements();
          },
        },
        {
          key: "locate-asset",
          label: "Locate in Grid",
          disabled: !onLocate,
          onClick: () => {
            if (onLocate) onLocate();
          },
        },
        {
          type: "divider",
        },
        {
          key: "add-metering-point",
          label: hasActiveAssetMeter ? "Create Meter (Already has meter)" : "Create Meter",
          disabled: hasActiveAssetMeter,
          onClick: () => onCreateMeter && onCreateMeter(asset.id),
        },
        {
          key: "unmap-meter",
          label: "Unmap Meter",
          danger: true,
          disabled: !hasActiveAssetMeter,
          onClick: () => onUnmapMeter && onUnmapMeter(asset.id, asset.description || asset.type || "Asset"),
        },
        {
          type: "divider",
        },
        {
          key: "delete-asset",
          label: "Delete Asset",
          danger: true,
          onClick: () => onDeleteAsset && onDeleteAsset(asset),
        },
      ]}
    />
  );
  return (
    <div
      ref={setNodeRef}
      style={{
        padding: 8,
        margin: 4,
        background: isDragging ? "#e6f7ff" : "#fafafa",
        border: "1px solid #1890ff",
        borderRadius: 4,
        transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
        boxShadow: isDragging ? "0 2px 8px #1890ff33" : undefined,
        width: "100%",
        maxWidth: "100%",
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
        <div
          {...listeners}
          {...attributes}
          style={{
            flex: 1,
            minWidth: 0, // This allows the flex item to shrink below its content size
            overflow: "hidden",
            cursor: "grab",
          }}
        >
          <div
            style={{
              fontWeight: "bold",
              fontSize: 13,
              lineHeight: "1.2",
              marginBottom: 2,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {asset.name && asset.name.trim() !== "" && <>{asset.name}</>}
          </div>
          <div
            style={{
              fontSize: 12,
              color: "#888",
              lineHeight: "1.2",
              marginBottom: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {positionName}
            {phases && (
              <span style={{ marginLeft: 8, fontWeight: "500" }}>
                • {phases} phase{phases !== 1 ? "s" : ""}
              </span>
            )}
          </div>
          {asset.description && (
            <div
              style={{
                fontSize: 12,
                lineHeight: "1.3",
                color: "#666",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {asset.description}
            </div>
          )}
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            flexShrink: 0, // Prevent icons from shrinking
            cursor: "default",
          }}
        >
          {/* Meter Status Indicator - Use passed meterIcon or fallback to simple indicator */}
          {meterIcon ||
            (hasMeter && (
              <Tooltip title="Has Active Meter">
                <ThunderboltOutlined
                  style={{
                    color: "#52c41a",
                    fontSize: 14,
                  }}
                />
              </Tooltip>
            ))}

          {/* Menu Button - All actions moved here */}
          {isPC && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              {/* Standalone View Measurements Button for CT Nodes */}
              {hasMeter && isCTNode && onViewMeterReadings && (
                <Tooltip title="View Measurements">
                  <Button
                    type="text"
                    size="small"
                    icon={<BarChartOutlined />}
                    onClick={onViewMeterReadings}
                    style={{
                      padding: "4px 6px",
                      minWidth: "auto",
                      height: "auto",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  />
                </Tooltip>
              )}
              <MenuDropdown menu={assetMenu} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function PlainAsset({
  asset,
  positionName,
  hasMeter,
  onViewMeasurements,
  onViewMeterReadings,
  isCTNode,
  meterIcon, // New prop for the meter icon with status
  // New props for menu functionality
  onEditAsset,
  onCreateMeter,
  onUnmapMeter,
  hasActiveAssetMeter,
  isPC,
  phases, // New prop for number of phases
  onDeleteAsset,
}: any) {
  if (asset.deletedAt) return null;

  // Create the asset menu with all actions
  const assetMenu = (
    <Menu
      style={{ minWidth: 180 }}
      items={[
        {
          key: "edit-asset",
          label: "Edit Asset",
          disabled: !onEditAsset,
          onClick: () => {
            if (onEditAsset) onEditAsset(asset);
          },
        },
        {
          type: "divider",
        },
        {
          key: "view-meter-readings",
          label: "View Meter Readings",
          disabled: !hasMeter || !onViewMeterReadings,
          onClick: () => {
            if (hasMeter && onViewMeterReadings) onViewMeterReadings();
          },
        },
        {
          key: "view-ct-measurements",
          label: "View CT Measurements",
          disabled: !hasMeter || !isCTNode || !onViewMeasurements,
          onClick: () => {
            if (hasMeter && isCTNode && onViewMeasurements) onViewMeasurements();
          },
        },
        {
          type: "divider",
        },
        {
          key: "add-metering-point",
          label: hasActiveAssetMeter ? "Create Meter (Already has meter)" : "Create Meter",
          disabled: hasActiveAssetMeter,
          onClick: () => onCreateMeter && onCreateMeter(asset.id),
        },
        {
          key: "unmap-meter",
          label: "Unmap Meter",
          danger: true,
          disabled: !hasActiveAssetMeter,
          onClick: () => onUnmapMeter && onUnmapMeter(asset.id, asset.description || asset.type || "Asset"),
        },
        {
          type: "divider",
        },
        {
          key: "delete-asset",
          label: "Delete Asset",
          danger: true,
          onClick: () => onDeleteAsset && onDeleteAsset(asset),
        },
      ]}
    />
  );

  return (
    <div
      style={{
        padding: 8,
        margin: 4,
        background: "#f5f5f5",
        border: "1px solid #d9d9d9",
        borderRadius: 4,
        color: "#888",
        cursor: "default",
        opacity: 0.85,
        width: "100%",
        maxWidth: "100%",
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
        <div
          style={{
            flex: 1,
            minWidth: 0, // This allows the flex item to shrink below its content size
            overflow: "hidden",
          }}
        >
          <div
            style={{
              fontWeight: "bold",
              fontSize: 13,
              lineHeight: "1.2",
              marginBottom: 2,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {asset.name && asset.name.trim() !== "" && <>{asset.name}</>}
          </div>
          <div
            style={{
              fontSize: 12,
              color: "#aaa",
              lineHeight: "1.2",
              marginBottom: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {positionName}
            {phases && (
              <span style={{ marginLeft: 8, fontWeight: "500" }}>
                • {phases} phase{phases !== 1 ? "s" : ""}
              </span>
            )}
          </div>
          {asset.description && (
            <div
              style={{
                fontSize: 12,
                lineHeight: "1.3",
                color: "#999",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {asset.description}
            </div>
          )}
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            flexShrink: 0, // Prevent icons from shrinking
          }}
        >
          {/* Meter Status Indicator - Use passed meterIcon or fallback to simple indicator */}
          {meterIcon ||
            (hasMeter && (
              <Tooltip title="Has Active Meter">
                <ThunderboltOutlined
                  style={{
                    color: "#52c41a",
                    fontSize: 14,
                  }}
                />
              </Tooltip>
            ))}

          {/* Menu Button - All actions moved here */}
          {isPC && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              {/* Standalone View Measurements Button for CT Nodes */}
              {hasMeter && isCTNode && onViewMeterReadings && (
                <Tooltip title="View Measurements">
                  <Button
                    type="text"
                    size="small"
                    icon={<BarChartOutlined />}
                    onClick={onViewMeterReadings}
                    style={{
                      padding: "4px 6px",
                      minWidth: "auto",
                      height: "auto",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  />
                </Tooltip>
              )}
              <MenuDropdown menu={assetMenu} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
